import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DLONotificationState, DLONotification } from '../../modules/dlo/types/notification.types';
import { fetchNotifications as fetchNotificationsService } from '../../modules/dlo/services/notificationService';

// Read persisted dismiss time from localStorage (web) so dismiss survives refresh
const persistedDismiss = typeof window !== 'undefined' && window.localStorage
  ? window.localStorage.getItem('notifications_last_dismiss_time')
  : null;
const persistedDismissTs = persistedDismiss ? Number(persistedDismiss) : null;

const initialState: DLONotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  lastFetchTime: null,
  lastDismissTime: persistedDismissTs,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    fetchNotificationsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchNotificationsSuccess: (state, action: PayloadAction<DLONotification[]>) => {
      // If user dismissed all notifications earlier, ignore any server notifications
      // that were created before that dismiss time so they don't reappear.
      const fetched = action.payload;
      const filtered = state.lastDismissTime
        ? fetched.filter(n => new Date(n.CREATED_AT).getTime() > (state.lastDismissTime || 0))
        : fetched;

      state.notifications = filtered;
      state.unreadCount = filtered.filter(n => !n.IS_READ).length;
      state.loading = false;
      state.lastFetchTime = Date.now();
    },
    fetchNotificationsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    markNotificationAsRead: (state, action: PayloadAction<number>) => {
      const notification = state.notifications.find(n => n.ID === action.payload);
      if (notification && !notification.IS_READ) {
        notification.IS_READ = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.IS_READ = true;
      });
      state.unreadCount = 0;
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      state.error = null;
      state.lastFetchTime = null;
      // record when user dismissed all so subsequent fetches ignore older items
      const ts = Date.now();
      state.lastDismissTime = ts;
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('notifications_last_dismiss_time', String(ts));
        }
      } catch (e) {
        // ignore storage errors
      }
    },
  },
});

export const {
  fetchNotificationsStart,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  clearNotifications,
} = notificationSlice.actions;

// Thunk to refetch notifications. If `userGpid` is not provided, derive it from
// the current Redux state so callers can simply `dispatch(refetchNotifications())`.
export const refetchNotifications = (userGpid?: string) => {
  return async (dispatch: any, getState: any) => {
    dispatch(fetchNotificationsStart());
    try {
      let gpid = userGpid;
      if (!gpid) {
        const state = getState();
        const appData = state?.appData;
        const authState = state?.auth;
        gpid = appData?.userInfo?.GPID || authState?.user?.claims?.employeeId || authState?.user?.employeeId || '55555555';
      }

      if (!gpid) {
        dispatch(fetchNotificationsFailure('Missing user GPID'));
        return;
      }

      const response = await fetchNotificationsService(gpid);
      dispatch(fetchNotificationsSuccess(response.data));
    } catch (error) {
      dispatch(fetchNotificationsFailure('Failed to fetch notifications'));
    }
  };
};

export default notificationSlice.reducer;
